import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Play, Instagram, Globe, CreditCard } from 'lucide-react';
import { heroData } from '../data/mock';

const HeroSection = () => {
  return (
    <section className="relative min-h-screen pt-28 md:pt-32 pb-16 overflow-hidden hero-grid">
      {/* Performance lines decoration */}
      <div className="performance-lines" />
      
      <div className="container mx-auto px-4 md:px-6">
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
          {/* Left Content */}
          <div className="max-w-xl">
            <div 
              className="inline-flex items-center gap-2 bg-[#12121A] border border-[#2A2A35] px-4 py-2 rounded-full mb-6 animate-fade-in-up"
            >
              <span className="w-2 h-2 rounded-full bg-[#FF6B35] animate-pulse" />
              <span className="text-sm font-medium text-[#B8B8C0]">Now accepting new clients</span>
            </div>
            
            <h1 className="display-large mb-6 animate-fade-in-up delay-100">
              Turn Your Social Media Into a{' '}
              <span className="gradient-text">High-Performance</span>{' '}
              Website.
            </h1>
            
            <p className="body-large mb-8 animate-fade-in-up delay-200">
              {heroData.subheadline}
            </p>
            
            <div className="flex flex-col sm:flex-row gap-4 mb-12 animate-fade-in-up delay-300">
              <Link 
                to="/contact"
                className="btn-primary"
              >
                {heroData.primaryCTA}
                <ArrowRight size={18} />
              </Link>
              <a 
                href="https://buckeyenation.emergent.host" 
                target="_blank" 
                rel="noopener noreferrer"
                className="btn-secondary"
              >
                <Play size={18} />
                {heroData.secondaryCTA}
              </a>
            </div>
            
            {/* Stats */}
            <div className="grid grid-cols-3 gap-4 animate-fade-in-up delay-400">
              {heroData.stats.map((stat, index) => (
                <div key={index} className="stat-card text-center">
                  <div className="text-2xl md:text-3xl font-bold text-white">{stat.value}</div>
                  <div className="text-xs md:text-sm text-[#6B6B78]">{stat.label}</div>
                </div>
              ))}
            </div>
          </div>
          
          {/* Right - Phone Mockups */}
          <div className="relative flex justify-center lg:justify-end">
            <div className="relative">
              {/* Main Phone */}
              <div className="phone-mockup w-64 md:w-72 animate-float">
                <div className="phone-screen aspect-[9/19] flex flex-col">
                  {/* Phone Header */}
                  <div className="bg-[#12121A] px-4 py-3 flex items-center justify-between border-b border-[#2A2A35]">
                    <span className="text-white text-xs font-medium">Your Website</span>
                    <Globe size={14} className="text-[#6B6B78]" />
                  </div>
                  
                  {/* Phone Content */}
                  <div className="flex-1 p-4 space-y-4 bg-[#0A0A0F]">
                    <div className="w-16 h-16 rounded-full bg-gradient-to-br from-[#FF6B35] to-[#FF8555] mx-auto" />
                    <div className="text-center">
                      <div className="h-3 w-24 bg-[#2A2A35] rounded mx-auto mb-2" />
                      <div className="h-2 w-32 bg-[#1A1A24] rounded mx-auto" />
                    </div>
                    <div className="space-y-2">
                      <div className="h-10 bg-gradient-to-r from-[#FF6B35] to-[#FF8555] rounded-lg flex items-center justify-center">
                        <span className="text-xs font-semibold text-black">Book Now</span>
                      </div>
                      <div className="h-10 bg-[#1A1A24] border border-[#2A2A35] rounded-lg flex items-center justify-center">
                        <span className="text-xs text-[#B8B8C0]">View Services</span>
                      </div>
                      <div className="h-10 bg-[#1A1A24] border border-[#2A2A35] rounded-lg flex items-center justify-center">
                        <span className="text-xs text-[#B8B8C0]">Contact</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              
              {/* Instagram Badge */}
              <div 
                className="absolute -left-4 top-1/4 bg-[#12121A] border border-[#2A2A35] rounded-xl p-3 shadow-lg animate-fade-in-up delay-200"
                style={{ animation: 'float 4s ease-in-out infinite 0.5s' }}
              >
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-[#833AB4] via-[#E1306C] to-[#F77737] flex items-center justify-center">
                    <Instagram size={20} className="text-white" />
                  </div>
                  <div>
                    <div className="text-xs font-semibold text-white">Instagram Bio</div>
                    <div className="text-xs text-[#6B6B78]">Click → Convert</div>
                  </div>
                </div>
              </div>
              
              {/* Booking Badge */}
              <div 
                className="absolute -right-4 bottom-1/3 bg-[#12121A] border border-[#FF6B35]/30 rounded-xl p-3 shadow-lg animate-fade-in-up delay-300 glow-orange"
                style={{ animation: 'float 4s ease-in-out infinite 1s' }}
              >
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-[#FF6B35] to-[#FF8555] flex items-center justify-center">
                    <CreditCard size={20} className="text-black" />
                  </div>
                  <div>
                    <div className="text-xs font-semibold text-white">+$2,450</div>
                    <div className="text-xs text-[#6B6B78]">This Week</div>
                  </div>
                </div>
              </div>
              
              {/* Progress indicator */}
              <div className="absolute -bottom-4 left-1/2 -translate-x-1/2 bg-[#12121A] border border-[#2A2A35] rounded-xl px-4 py-2 shadow-lg">
                <div className="flex items-center gap-2">
                  <div className="w-2 h-2 rounded-full bg-[#FF6B35]" />
                  <span className="text-xs font-medium text-white">Conversion Rate: 4.2%</span>
                </div>
                <div className="progress-bar mt-2 w-32">
                  <div className="progress-bar-fill" style={{ width: '72%' }} />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default HeroSection;
